/**
 * Envío del FEEDBACK que escribe una persona.
 *
 * Va al servidor de juego (`POST /feedback`), que es quien lo entrega — por
 * correo o por webhook, según cómo esté configurado el despliegue (ver
 * `packages/server/DEPLOY.md`). El navegador no puede mandar un correo por su
 * cuenta, así que sin backend no hay envío: y eso hay que DECIRLO, no fingirlo.
 *
 * Por eso el resultado distingue tres finales distintos, y cada uno se enseña
 * con palabras distintas:
 *
 *  - `enviado` — salió de verdad; hay alguien que lo va a leer.
 *  - `sin-servidor` — este despliegue no tiene a dónde mandarlo (o no hay
 *    backend configurado). No es un fallo: es que hace falta el correo de quien
 *    escribe.
 *  - `error` — había a dónde mandarlo y no se pudo. Se reintenta o se copia.
 *
 * Lo único inaceptable sería un "✓ enviado" que en realidad no sale de aquí: la
 * persona se queda creyendo que lo contó y nadie lo lee nunca.
 */
import { regionHttpBase } from "./regions";

export type FeedbackKind = "bug" | "idea" | "opinion";

export type FeedbackResult = "enviado" | "sin-servidor" | "error";

export interface FeedbackPayload {
  kind: FeedbackKind;
  text: string;
  /** Bloque técnico ya compuesto (vacío si la persona lo quitó). */
  diagnostics: string;
}

/** Cuánto se espera al servidor antes de ofrecer el correo. */
const TIMEOUT_MS = 8000;

export async function sendFeedback(
  payload: FeedbackPayload,
  base = regionHttpBase(),
  doFetch: typeof fetch = fetch,
): Promise<FeedbackResult> {
  if (!base) return "sin-servidor";
  const ctrl = typeof AbortController === "function" ? new AbortController() : undefined;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : undefined;
  try {
    const res = await doFetch(`${base}/feedback`, {
      method: "POST",
      // `text/plain` evita el preflight CORS (el cuerpo sigue siendo JSON), el
      // mismo truco que usa la ingesta de errores.
      headers: { "content-type": "text/plain" },
      body: JSON.stringify(payload),
      signal: ctrl?.signal,
    });
    if (res.ok) return "enviado";
    // 501 = el servidor existe pero no tiene proveedor de entrega configurado.
    return res.status === 501 ? "sin-servidor" : "error";
  } catch {
    return "error";
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
